import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
} from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { addReason, getReasonDataById, updateReason } from "model/ReasonModel";

/* -------------------- Yup Schema -------------------- */
const reasonSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .required("Title is required"),
  status: Yup.boolean(),
});

const ReasonAddEditModal = ({
  setReasonAddEditModalOpen,
  reasonAddEditModalOpen,
  getReasonList,
  itemId,
  setFormData,
  formData,
  totalData
}) => {
  const [formKey, setFormKey] = useState(0);
  const [errors, setErrors] = useState({});

  const closeModal = () => {
    setReasonAddEditModalOpen(false);
    setErrors({});
  };

  /* -------------------- Change Handler -------------------- */
  const handleChange = async (e) => {
    const { name, type, checked, value } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));

    if (!reasonSchema.fields[name]) return;

    try {
      await Yup.reach(reasonSchema, name).validate(fieldValue);
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  /* -------------------- Submit -------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await reasonSchema.validate(formData, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return;
    }

    const params = {
      title: formData?.title,
      candidateDeactive: Boolean(formData?.candidateDeactive),
      forTools: Boolean(formData?.forTools),
      status: formData?.status ? "active" : "deactive",
      // fallback keys for old contracts
      name: formData?.title,
      jobCategoryName: formData?.title,
      jobCategoryStatus: formData?.status ? "active" : "deactive",
    };

    let response;
    if (itemId) {
      response = await updateReason(itemId, params);
    } else {
      response = await addReason(params);
    }

    if (response?.success) {
      toast.success(response?.message);
      closeModal();
      getReasonList();
    } else {
      toast.error(response?.message);
    }
  };

  /* -------------------- Edit Fetch -------------------- */
  const getFetchReasonById = async (id) => {
    const response = await getReasonDataById({ Id: id });
    if (response?.data) {
      setFormData({
        title:
          response?.data?.title ||
          response?.data?.name ||
          response?.data?.jobCategoryName ||
          "",
        candidateDeactive:
          Boolean(response?.data?.candidateDeactive) ||
          Boolean(response?.data?.isCandidateDeactive),
        forTools:
          Boolean(response?.data?.forTools) ||
          Boolean(response?.data?.isForTools),
        status:
          response?.data?.status === "active" ||
          response?.data?.jobCategoryStatus === "active",
      });
      setFormKey((k) => k + 1);
    }
  };

  useEffect(() => {
    if (itemId) {
      getFetchReasonById(itemId);
    }
  }, [itemId]);

  return (
    <Modal
      isOpen={reasonAddEditModalOpen}
      toggle={closeModal}
      backdrop="static"
      className="modal-full-height"
      centered
    >
      <ModalBody
        key={formKey}
        className="p-4"
        style={{ maxHeight: "95vh", overflowY: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">{itemId? 'Edit' : 'Create'} Reason </h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        <Form onSubmit={handleSubmit}>
          {/* Title */}
          <FormGroup>
            <Label>Title <span className="text-danger">*</span></Label>
            <Input
              type="text"
              name="title"
              defaultValue={formData?.title}
              onChange={handleChange}
            />
            {errors.title && (
              <small className="text-danger">{errors.title}</small>
            )}
          </FormGroup>

          {/* Candidate Deactive */}
          <FormGroup>
            <Label>Candidate Deactive <span className="text-danger">*</span></Label>
            <div className="input-box d-flex align-items-center justify-content-between">
              <span className="text-muted">Type</span>
              <div>
                <Input
                  type="radio"
                  name="candidateDeactive"
                  defaultChecked={formData?.candidateDeactive === true}
                  onChange={() =>
                    setFormData((p) => ({ ...p, candidateDeactive: true }))
                  }
                />{" "}
                Yes
                <Input
                  type="radio"
                  name="candidateDeactive"
                  defaultChecked={formData?.candidateDeactive === false}
                  onChange={() =>
                    setFormData((p) => ({ ...p, candidateDeactive: false }))
                  }
                  className="ms-3"
                />{" "}
                No
              </div>
            </div>
          </FormGroup>

          {/* For Tools */}
          <FormGroup>
            <Label>For Tools</Label>
            <div className="input-box d-flex align-items-center justify-content-between">
              <span className="text-muted">Type</span>
              <div>
                <Input
                  type="radio"
                  name="forTools"
                  defaultChecked={formData?.forTools === true}
                  onChange={() =>
                    setFormData((p) => ({ ...p, forTools: true }))
                  }
                />{" "}
                Yes
                <Input
                  type="radio"
                  name="forTools"
                  defaultChecked={formData?.forTools === false}
                  onChange={() =>
                    setFormData((p) => ({ ...p, forTools: false }))
                  }
                  className="ms-3"
                />{" "}
                No
              </div>
            </div>
          </FormGroup>

          {/* Status */}
          <FormGroup>
            <Label>Status <span className="text-danger">*</span></Label>
            <div className="input-box d-flex align-items-center justify-content-between">
              <span className="text-muted">Type</span>
              <div>
                <Input
                  type="radio"
                  name="status"
                  defaultChecked={formData?.status === true}
                  onChange={() =>
                    setFormData((p) => ({ ...p, status: true }))
                  }
                />{" "}
                Active
                <Input
                  type="radio"
                  name="status"
                  defaultChecked={formData?.status === false}
                  onChange={() =>
                    setFormData((p) => ({ ...p, status: false }))
                  }
                  className="ms-3"
                />{" "}
                Deactive
              </div>
            </div>
          </FormGroup>

          <div className="text-end mt-3">
            <Button color="primary" type="submit">
            {itemId? 'Update' : 'Save'}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ReasonAddEditModal;
